import { nearestPoint, nextInDirection, type Point } from './spatialNavigation';

/**
 * A gamepad on the messages and menus of the DOM: whatever is showing over the board that asks
 * the player something, and the home screen itself. The pad moves the keyboard focus between the
 * controls in the topmost of them, the same focus a Tab key would move, so the look is the one
 * the browser already has; A presses what is focused and B presses the safe way out.
 */

export interface Message {
  root: HTMLElement;
  /** A modal message keeps the pad until it is answered; a plate beside the board does not. */
  modal: boolean;
}

/** Over-the-board surfaces, topmost first: the first selector that matches wins. */
const MESSAGE_SELECTORS: readonly { selector: string; modal: boolean }[] = [
  { selector: '[role="alertdialog"]', modal: true },
  { selector: '.card-battle[role="dialog"]', modal: true },
  { selector: '.settings-dialog', modal: true },
  { selector: '.menu[role="dialog"]', modal: true },
  { selector: '.overlay.battle', modal: true },
  { selector: '.plate.guide', modal: false },
];

const FOCUSABLE = 'button:not(:disabled), a[href], select:not(:disabled), input:not(:disabled)';
/** Labels of the buttons that back out of a message without doing anything. */
const SAFE_LABELS = [
  'cancel',
  'pass',
  'got it',
  'back',
  'close',
  'no support',
  'resume',
  'ok',
  'decline',
];
export const FOCUS_CLASS = 'pad-focus';

/** The message on top of the board right now, if any. */
export function topMessage(root: ParentNode = document): Message | null {
  for (const { selector, modal } of MESSAGE_SELECTORS) {
    const found = Array.from(root.querySelectorAll<HTMLElement>(selector)).filter(visible);
    const element = found[found.length - 1];
    if (element) return { root: element, modal };
  }
  return null;
}

/** Drawn on the page at all: a hidden control cannot be reached and must not be focused. */
export function visible(element: HTMLElement): boolean {
  const rect = element.getBoundingClientRect();
  return rect.width > 0 && rect.height > 0;
}

/** A slider (an `<input type="range">`), which the pad nudges rather than presses. */
export function isSlider(element: HTMLElement | null): element is HTMLInputElement {
  return (
    typeof HTMLInputElement !== 'undefined' &&
    element instanceof HTMLInputElement &&
    element.type === 'range'
  );
}

/**
 * Moves a slider by `direction` steps of `amount` the way a finger would, so the framework that
 * owns it (React tracks the value it last set) sees a real change and updates the game.
 */
export function nudgeSlider(input: HTMLInputElement, direction: number, amount: number): void {
  const min = Number(input.min || 0);
  const max = Number(input.max || 1);
  const next = Math.min(max, Math.max(min, Number(input.value) + direction * amount));
  const setter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')?.set;
  if (setter) setter.call(input, String(next));
  else input.value = String(next);
  input.dispatchEvent(new Event('input', { bubbles: true }));
  input.dispatchEvent(new Event('change', { bubbles: true }));
}

function controlsOf(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(visible);
}

function centre(element: HTMLElement): Point {
  const rect = element.getBoundingClientRect();
  return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
}

function labelOf(element: HTMLElement): string {
  return (element.getAttribute('aria-label') ?? element.textContent ?? '').trim().toLowerCase();
}

/** The control focused inside `root`, if the focus is there. */
export function focusedIn(root: HTMLElement): HTMLElement | null {
  const active = document.activeElement;
  return active instanceof HTMLElement && root.contains(active) && active !== root ? active : null;
}

/** Puts the focus on a control and marks it, so a pad user sees where they are. */
export function focusControl(element: HTMLElement | null): void {
  for (const marked of document.querySelectorAll(`.${FOCUS_CLASS}`))
    if (marked !== element) marked.classList.remove(FOCUS_CLASS);
  if (!element) return;
  element.classList.add(FOCUS_CLASS);
  element.focus({ preventScroll: false });
}

/** The safe way out of a message: its cancel-like button, else its first control. */
export function safeControl(root: HTMLElement): HTMLElement | null {
  const controls = controlsOf(root);
  const safe = controls.find((c) => SAFE_LABELS.includes(labelOf(c)));
  return safe ?? controls[0] ?? null;
}

/** Makes sure something in the message is focused; the safe control first, as a keyboard would. */
export function ensureFocus(root: HTMLElement): HTMLElement | null {
  const current = focusedIn(root);
  if (current) {
    current.classList.add(FOCUS_CLASS);
    return current;
  }
  const first = safeControl(root);
  focusControl(first);
  return first;
}

/** Moves the focus to the control that lies in `direction` from the focused one. */
export function moveFocus(root: HTMLElement, direction: Point): HTMLElement | null {
  const controls = controlsOf(root);
  if (controls.length === 0) return null;
  const current = focusedIn(root);
  if (!current) return ensureFocus(root);
  const points = controls.map(centre);
  const from = controls.indexOf(current);
  const at = from >= 0 ? points[from]! : centre(current);
  const next = nextInDirection(points, at, direction, (i) => i === from);
  if (next === null) return current;
  focusControl(controls[next]!);
  return controls[next]!;
}

/** The step a push makes through a list: along its stronger axis, right and down forward. */
function listStep(direction: Point): number {
  return Math.abs(direction.x) >= Math.abs(direction.y)
    ? Math.sign(direction.x)
    : Math.sign(direction.y);
}

/**
 * Presses the focused control: a click, or a step through a select's options. A press goes round
 * the options; a push steps along its own axis and stops at the ends. A press used to stop at the
 * last option, with no way back from the pad (client-input:bugs#3). Disabled options are passed.
 */
export function activateFocused(root: HTMLElement, direction?: Point): boolean {
  const current = focusedIn(root);
  if (!current) return false;
  if (current instanceof HTMLSelectElement) {
    const options = current.options;
    const count = options.length;
    const step = direction ? listStep(direction) : 1;
    let next = current.selectedIndex;
    for (let i = 0; i < count && step !== 0; i++) {
      next = direction ? next + step : (next + step + count) % count;
      if (next < 0 || next >= count) return true;
      if (!options[next]!.disabled) break;
    }
    if (next === current.selectedIndex || options[next]?.disabled !== false) return true;
    current.selectedIndex = next;
    current.dispatchEvent(new Event('change', { bubbles: true }));
    return true;
  }
  current.click();
  return true;
}

/**
 * A push in a message or the sound panel: left and right turn a focused list's option, any other
 * push moves the focus, so the pad can both change a list and leave it (client-input:bugs#3).
 */
export function stepFocus(root: HTMLElement, direction: Point): void {
  if (focusedIn(root) instanceof HTMLSelectElement && direction.y === 0)
    activateFocused(root, direction);
  else moveFocus(root, direction);
}

/** The control nearest a point on the screen, for a cursor arriving from the board. */
export function nearestControl(root: HTMLElement, at: Point): HTMLElement | null {
  const controls = controlsOf(root);
  const index = nearestPoint(controls.map(centre), at);
  return index === null ? null : controls[index]!;
}
